import type { CompanyOption } from '@shared/components/Modals/FollowCompanyModal.types';
import { CheckCircle, ErrorOutline, Handshake } from '@mui/icons-material';
import ArticleIcon from '@mui/icons-material/Article';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@shared/components/Buttons/Button';
import CompanySearchAutocomplete from '@/shared/components/CompanySearch/CompanySearchAutocomplete';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext';
import {
  HelpText,
  SectionTitle,
  StepContent,
  StyledDialogActions,
} from '@shared/styles/ModalStyles';
import { useBreakpoints } from '@shared/theme';
import { EngagementType } from '../../intelligence-requests/components/IntelligenceRequest.types';

interface CreateIntelligenceRequestModalProps {
  companyId?: number;
  targetUserId?: number;
  reviewId?: number;
  onSuccess?: () => void;
  isEditMode?: boolean;
  intelligenceRequestId?: string;
  defaultDescription?: string;
  defaultAmountUsd?: number;
  forcePhoneOnly?: boolean;
}

const CreateIntelligenceRequestModal: React.FC<
  CreateIntelligenceRequestModalProps
> = ({
  companyId,
  targetUserId,
  reviewId: reviewIdProp,
  onSuccess,
  isEditMode = false,
  intelligenceRequestId,
  defaultDescription = '',
  defaultAmountUsd = 150,
  forcePhoneOnly = false,
}) => {
  const { reviewId: reviewIdParam } = useParams<{ reviewId?: string }>();
  let resolvedReviewId
    = reviewIdProp ?? (reviewIdParam ? Number(reviewIdParam) : undefined);

  if (resolvedReviewId == null && typeof window !== 'undefined') {
    const match = window.location.pathname.match(
      /\/review-reader\/[^/]+\/(\d+)/,
    );
    if (match?.[1]) {
      const n = Number(match[1]);
      resolvedReviewId = Number.isFinite(n) ? n : undefined;
    }
  }

  const [description, setDescription] = useState(defaultDescription);
  const [amountUsd, setAmountUsd] = useState<string | number>(defaultAmountUsd);
  const [step, setStep] = useState<'form' | 'confirm' | 'accepted'>('form');
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [engagementType, setEngagementType] = useState<EngagementType>(
    targetUserId ? EngagementType.Direct : EngagementType.Direct,
  );
  const [authorHourlyRate, setAuthorHourlyRate] = useState<number | null>(null);

  const { user, fetchUsageInfo } = useContext(UserContext);
  const api = useApi();
  const { closeMessage } = useMessageService();
  const { showSnackbar } = useSnackbar();
  const { isMobile } = useBreakpoints();

  const hasNoIRsLeft
    = (user?.intelligenceRequestsAvailableInSubPeriod ?? 0) <= 0;
  const shouldShowBounty = hasNoIRsLeft || isEditMode;

  useEffect(() => {
    document.body.classList.add('intelligence-request-modal-open');
    return () => {
      document.body.classList.remove('intelligence-request-modal-open');
    };
  }, []);

  useEffect(() => {
    if (companyId) {
      api
        .get(`/company/${companyId}`)
        .then((response) => {
          if (response.success && response.data) {
            const company = {
              id: response.data.id,
              label: response.data.companyName,
              url: response.data.url,
            };
            setSelectedCompany(company);
          }
        })
        .catch(() => {
        });
    }
  }, [companyId]);

  // Fetch author's hourly rate when targetUserId is provided
  useEffect(() => {
    if (targetUserId && !isEditMode) {
      api
        .get(`/user/by-id/${targetUserId}`)
        .then((response) => {
          if (response?.hourlyRate) {
            setAuthorHourlyRate(response.hourlyRate);
            setAmountUsd(response.hourlyRate);
          }
        })
        .catch(() => {
        });
    }
  }, [targetUserId, isEditMode]);

  const handleCompanyChange = (company: CompanyOption | null) => {
    setSelectedCompany(company);
  };

  const baseMinimumIncentiveUsd = 50;
  const minimumIncentiveUsd = authorHourlyRate && authorHourlyRate > baseMinimumIncentiveUsd
    ? authorHourlyRate
    : baseMinimumIncentiveUsd;
  const maximumIncentiveUsd = 10_000;
  const descLimit = 350;

  const numericAmount
    = typeof amountUsd === 'number'
      ? amountUsd
      : amountUsd === ''
        ? 0
        : Number(amountUsd) || 0;
  const isAmountValid
    = numericAmount >= minimumIncentiveUsd
      && numericAmount <= maximumIncentiveUsd;
  const amountError
    = !isAmountValid && typeof amountUsd === 'string' && amountUsd !== ''
      ? authorHourlyRate && numericAmount < authorHourlyRate
        ? `Amount cannot be below the consultant's hourly rate of $${authorHourlyRate}`
        : `Amount must be between $${minimumIncentiveUsd} and $${maximumIncentiveUsd}`
      : '';

  const isCompanyValid = targetUserId || !!(selectedCompany?.id || companyId);
  const isFormValid = isCompanyValid;

  const saveIntelligenceRequest = async () => {
    const targetCompanyId = selectedCompany?.id || companyId;
    const payload = {
      userId: user?.id,
      ...(targetCompanyId && { companyId: targetCompanyId }),
      description: description.trim(),
      engagementType,
      amountUsd: numericAmount,
      ...(resolvedReviewId && { reviewId: resolvedReviewId }),
      ...(targetUserId && { authorId: targetUserId }),
      isPrivate: !!targetUserId,
    };

    try {
      let result;
      if (isEditMode && intelligenceRequestId) {
        result = await api.patch(
          `/intelligence-request/${intelligenceRequestId}`,
          payload,
        );
      }
      else {
        result = await api.post('/intelligence-request', payload);
      }

      if (!result.success) {
        showSnackbar(
          `An error occurred while ${isEditMode ? 'updating' : 'creating'} the request.`,
          'error',
        );
        return;
      }

      setStep('accepted');

      await fetchUsageInfo();

      setTimeout(() => {
        showSnackbar(
          isEditMode
            ? 'Intelligence request updated successfully'
            : 'Intelligence request created and is in pending status',
          'success',
        );
        onSuccess?.();
      }, 300);
    }
    catch (error) {
      showSnackbar(
        `An error occurred while ${isEditMode ? 'updating' : 'creating'} the request.`,
        'error',
      );
    }
  };

  const handleContinue = () => {
    void saveIntelligenceRequest();
  };

  return (
    <Box className="create-intelligence-request-modal">
      <StepContent>
        {step === 'form' && (
          <>
            <Box className="intro-banner">
              <Box className="icon-circle">
                <Handshake />
              </Box>
              <Box>
                <Typography variant="subtitle1" className="banner-title">
                  {isEditMode
                    ? 'Edit Intelligence Request'
                    : 'Request Intelligence'}
                </Typography>
                <Typography variant="body2" className="banner-text">
                  {isEditMode
                    ? 'Update the details of your intelligence request below.'
                    : forcePhoneOnly
                      ? 'Request a phone consultation to gather insights directly from this seller.'
                      : targetUserId
                        ? 'Request intelligence from this seller - choose between a phone consultation or a written review.'
                        : 'Choose how you want to gather insights - either through a phone consultation or a detailed written review.'}
                </Typography>
              </Box>
            </Box>

            {!isEditMode && (
              <Box className="form-section">
                <SectionTitle variant="subtitle1" gutterBottom>
                  Engagement Type
                </SectionTitle>
                <ToggleButtonGroup
                  value={engagementType}
                  exclusive
                  onChange={(_, value) => value && setEngagementType(value)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value={EngagementType.Direct}>
                    <PhoneIcon sx={{ mr: 1 }} />
                    Phone Consultation
                  </ToggleButton>
                  <Tooltip
                    title={
                      forcePhoneOnly
                        ? 'Direct consultation requests from the button are phone-only. Use the dropdown menu to request a written review.'
                        : ''
                    }
                    arrow
                  >
                    <span style={{ flex: 1 }}>
                      <ToggleButton
                        value={EngagementType.Review}
                        disabled={forcePhoneOnly}
                        sx={{ width: '100%' }}
                      >
                        <ArticleIcon sx={{ mr: 1 }} />
                        Written Review
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </ToggleButtonGroup>
                <HelpText variant="caption">
                  {forcePhoneOnly
                    ? 'Direct consultation requests are phone-only to enable real-time conversation.'
                    : engagementType === EngagementType.Direct
                      ? 'A direct phone consultation allows you to have a real-time conversation with sellers who have experience with this company.'
                      : 'Request a detailed written review from sellers who have worked with this company.'}
                </HelpText>
              </Box>
            )}

            {!targetUserId && (
              <Box className="form-section">
                <Box className="content-card company-search-field">
                  <CompanySearchAutocomplete
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    label="Target Company"
                    placeholder="Type or select a company"
                    helperText={
                      companyId
                        ? 'Company is pre-selected from context and cannot be changed.'
                        : 'Pick from our existing company list or type a new one.'
                    }
                    disabled={!!companyId}
                    data-testid="company-autocomplete"
                    allowNewCompany={!companyId}
                  />
                </Box>
              </Box>
            )}

            <Box className="form-section">
              <SectionTitle variant="subtitle1" gutterBottom>
                Notes for Author
              </SectionTitle>
              <Box className="content-card notes-field">
                <TextField
                  placeholder={
                    isMobile
                      ? 'Describe your criteria briefly...'
                      : 'Describe your criteria, e.g. \'I want to dig into XYZ topic area\''
                  }
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  inputProps={{ maxLength: descLimit }}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <Box className="field-footer">
                  <HelpText variant="caption" className="help-text">
                    {isMobile
                      ? 'Give context to help author respond.'
                      : 'Share any context that will help the author tailor their response.'}
                  </HelpText>
                  <HelpText variant="caption" className="help-text">
                    {description.length}
                    /
                    {descLimit}
                  </HelpText>
                </Box>
              </Box>
            </Box>

            {shouldShowBounty && (
              <Box className="form-section">
                <SectionTitle variant="subtitle1" gutterBottom>
                  Payment
                </SectionTitle>
                <Box className="content-card">
                  <TextField
                    label="Amount (USD)"
                    value={amountUsd}
                    onChange={e => setAmountUsd(e.target.value)}
                    type="number"
                    fullWidth
                    error={!!amountError}
                    helperText={
                      amountError
                      || `Set a payment between $${minimumIncentiveUsd} and $${maximumIncentiveUsd}`
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}

        {step === 'confirm' && (
          <>
            <Box className="confirm-banner">
              <Box className="warning-icon">
                <ErrorOutline />
              </Box>
              <Box>
                <Typography variant="subtitle1" className="banner-title">
                  Confirm your request
                </Typography>
                <Typography variant="body2" className="banner-text">
                  Submitting will consume one intelligence request credit from
                  your account.
                </Typography>
              </Box>
            </Box>
            <Box className="content-card">
              <Box className="summary-grid">
                {selectedCompany && (
                  <Box>
                    <SectionTitle
                      variant="subtitle2"
                      className="summary-item-label"
                    >
                      Target Company
                    </SectionTitle>
                    <Typography variant="body2">
                      {selectedCompany.label}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <SectionTitle
                    variant="subtitle2"
                    className="summary-item-label"
                  >
                    Payment (USD)
                  </SectionTitle>
                  <Typography variant="body2">
                    $
                    {numericAmount || 0}
                  </Typography>
                </Box>
                <Box>
                  <SectionTitle
                    variant="subtitle2"
                    className="summary-item-label"
                  >
                    Notes for Author
                  </SectionTitle>
                  <Typography variant="body2">
                    {description?.trim() || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {step === 'accepted' && (
          <Box className="content-card">
            <Box className="success-state">
              <Box className="success-icon" aria-label="accepted">
                <CheckCircle />
              </Box>
              <Typography variant="h5" className="success-title">
                {isEditMode ? 'Request updated' : 'Request submitted'}
              </Typography>
              <Typography variant="body1" className="success-description">
                {isEditMode
                  ? 'Your intelligence request has been updated successfully.'
                  : 'A Kandir team admin will review and approve your consultation request. After approval:'}
              </Typography>
              {!isEditMode && (
                <>
                  <ul className="success-list">
                    <li>The author will be notified of your request</li>
                    <li>
                      The author may accept or decline based on availability and
                      scope
                    </li>
                    <li>
                      You'll receive confirmation once the author responds
                    </li>
                  </ul>
                  <Typography variant="body1" className="success-description">
                    You'll also get an email with the outcome. If accepted,
                    follow-up steps will be coordinated.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}
      </StepContent>

      <StyledDialogActions>
        {step === 'accepted'
          ? (
              <>
                <span />
                <Button size="small" color="primary" onClick={closeMessage}>
                  Close
                </Button>
              </>
            )
          : step === 'confirm'
            ? (
                <>
                  <Button size="small" onClick={() => setStep('form')}>
                    Back
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => void saveIntelligenceRequest()}
                  >
                    {isEditMode ? 'Save' : 'Submit'}
                  </Button>
                </>
              )
            : (
                <>
                  <Button size="small" onClick={closeMessage}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    onClick={handleContinue}
                    disabled={!isFormValid}
                  >
                    {isEditMode ? 'Save' : 'Continue'}
                  </Button>
                </>
              )}
      </StyledDialogActions>
    </Box>
  );
};

export default CreateIntelligenceRequestModal;
